import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { BotHandle } from './bot3d/scene';

const EYE = 'oklch(0.84 0.12 215)';
const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const hasWebGL = () => {
  try {
    return Boolean(document.createElement('canvas').getContext('webgl2'));
  } catch {
    return false;
  }
};

/**
 * The workspace's mascot: a studio-lit 3D agent core (three.js, loaded on demand) that turns toward
 * the pointer, blinks and floats, scans and spins its orbit ring while runs are in progress, and on a
 * click hops, smiles and says the next line of `messages`. Without WebGL2 it falls back to a flat
 * SVG version of the same drone.
 */
export function AgentBot({
  busy,
  messages,
  className,
}: {
  /** Runs are in progress: the antenna pulses fast and the mouth types. */
  busy: boolean;
  /** What it says, one per click (the first shows on its own). */
  messages: string[];
  className?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bot = useRef<BotHandle | null>(null);
  const [mode, setMode] = useState<'loading' | '3d' | 'flat'>(() =>
    hasWebGL() ? 'loading' : 'flat',
  );
  const [look, setLook] = useState({ x: 0, y: 0 });
  const [happy, setHappy] = useState(false);
  const [index, setIndex] = useState(0);
  const [bubble, setBubble] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);

  // The 3D scene loads with the page's idle time; any failure falls back to the flat bot.
  useEffect(() => {
    if (mode === 'flat') return;
    let cancelled = false;
    import('./bot3d/scene')
      .then(({ createBot }) => {
        if (cancelled || !canvasRef.current) return;
        bot.current = createBot(canvasRef.current);
        setMode('3d');
      })
      .catch(() => !cancelled && setMode('flat'));
    return () => {
      cancelled = true;
      bot.current?.dispose();
      bot.current = null;
    };
    // Created once; `mode` only changes from here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bot.current?.setBusy(busy);
  }, [busy, mode]);

  // Flat bot: eyes follow the pointer anywhere on the page, one update per frame.
  useEffect(() => {
    if (mode !== 'flat' || reducedMotion()) return;
    let frame = 0;
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const dx = e.clientX - (rect.left + rect.width / 2);
        const dy = e.clientY - (rect.top + rect.height * 0.45);
        const clamp = (v: number) => Math.max(-1, Math.min(1, v));
        setLook({ x: clamp(dx / 320), y: clamp(dy / 240) });
      });
    };
    window.addEventListener('pointermove', onMove);
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(frame);
    };
  }, [mode]);

  const say = (next: number) => {
    setIndex(next);
    setBubble(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setBubble(false), 7000);
  };

  // Greets once the page has settled.
  useEffect(() => {
    const t = window.setTimeout(() => say(0), 700);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(hideTimer.current);
    };
  }, []);

  const message = messages.length ? messages[index % messages.length] : null;

  return (
    <div className={cn('bot-anim relative flex flex-col items-center', className)}>
      <div className="relative h-20 w-full max-w-64" aria-live="polite">
        {bubble && message && (
          <p
            key={index}
            className="bg-popover text-popover-foreground animate-in fade-in zoom-in-95 slide-in-from-bottom-1 absolute inset-x-0 bottom-2 rounded-2xl border px-3.5 py-2.5 text-center text-sm text-balance shadow-lg duration-300"
          >
            {message}
            <span
              className="bg-popover absolute -bottom-1.5 left-1/2 size-3 -translate-x-1/2 rotate-45 border-r border-b"
              aria-hidden
            />
          </p>
        )}
      </div>
      <button
        ref={ref}
        type="button"
        aria-label="Agent bot: click for a summary of today"
        title="Click me"
        onClick={() => {
          bot.current?.celebrate();
          setHappy(true);
          window.setTimeout(() => setHappy(false), 1400);
          say(bubble ? index + 1 : index);
        }}
        className="focus-visible:ring-ring/50 cursor-pointer rounded-3xl transition-transform outline-none focus-visible:ring-3 active:scale-[0.98]"
      >
        {mode !== 'flat' ? (
          <canvas
            ref={canvasRef}
            className={cn(
              'block h-64 w-64 transition-opacity duration-700 md:h-80 md:w-80',
              mode === '3d' ? 'opacity-100' : 'opacity-0',
            )}
            aria-hidden
          />
        ) : (
          <svg viewBox="0 0 200 200" className="h-auto w-48 overflow-visible md:w-56" aria-hidden>
            <defs>
              <radialGradient id="drone-shell" cx="38%" cy="30%" r="75%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="70%" stopColor="#e4e6eb" />
                <stop offset="100%" stopColor="#b9bcc6" />
              </radialGradient>
            </defs>
            <ellipse
              cx="100"
              cy="190"
              rx="42"
              ry="5"
              className="fill-foreground"
              style={{
                transformOrigin: '100px 190px',
                animation: 'bot-shadow 3.4s ease-in-out infinite',
              }}
            />
            <g style={{ animation: 'bot-float 3.4s ease-in-out infinite' }}>
              <g
                style={{
                  transformOrigin: '100px 92px',
                  animation: happy ? 'bot-wave 0.9s ease-in-out' : undefined,
                }}
              >
                {/* Orbit ring, back half (behind the core) */}
                <path
                  d="M 16 112 A 84 22 -6 0 1 184 96"
                  fill="none"
                  stroke="#2b2f38"
                  strokeWidth="3"
                />
                {/* Side pods */}
                <rect x="28" y="78" width="14" height="30" rx="6" fill="#2b2f38" />
                <rect x="158" y="78" width="14" height="30" rx="6" fill="#2b2f38" />
                <circle cx="35" cy="93" r="6" fill="none" stroke={EYE} strokeWidth="2.5" />
                <circle cx="165" cy="93" r="6" fill="none" stroke={EYE} strokeWidth="2.5" />
                {/* Core */}
                <circle cx="100" cy="92" r="62" fill="url(#drone-shell)" />
                <rect x="88" y="27" width="24" height="5" rx="2.5" fill="#2b2f38" />
                <circle cx="100" cy="28" r="3" fill={EYE} />
                {/* Visor */}
                <rect x="50" y="72" width="100" height="40" rx="20" fill="#0d0e13" />
                <rect
                  x="50"
                  y="72"
                  width="100"
                  height="40"
                  rx="20"
                  fill="none"
                  stroke="#2b2f38"
                  strokeWidth="2"
                />
                <g
                  style={{
                    transform: `translate(${look.x * 14}px, ${look.y * 5}px)`,
                    transition: 'transform 140ms ease-out',
                  }}
                >
                  {happy ? (
                    <path
                      d="M 86 98 q 14 -18 28 0"
                      stroke={EYE}
                      strokeWidth="6"
                      strokeLinecap="round"
                      fill="none"
                    />
                  ) : (
                    <rect
                      x="78"
                      y={busy ? 88 : 85}
                      width="44"
                      height={busy ? 8 : 14}
                      rx={busy ? 4 : 7}
                      fill={EYE}
                      style={{
                        transformBox: 'fill-box',
                        transformOrigin: 'center',
                        animation: 'bot-blink 5.5s ease-in-out infinite',
                      }}
                    />
                  )}
                </g>
                {/* Orbit ring, front half, with satellites */}
                <path
                  d="M 16 112 A 84 22 -6 0 0 184 96"
                  fill="none"
                  stroke="#2b2f38"
                  strokeWidth="3"
                />
                <circle cx="62" cy="129" r="5" fill="url(#drone-shell)" />
                <circle cx="62" cy="129" r="2" fill={EYE} />
                <circle cx="150" cy="122" r="4" fill="url(#drone-shell)" />
                <circle cx="150" cy="122" r="1.6" fill={EYE} />
                {/* Hover ring */}
                <ellipse
                  cx="100"
                  cy="162"
                  rx="17"
                  ry="4"
                  fill="none"
                  stroke={EYE}
                  strokeWidth="2.5"
                  style={{ animation: `bot-glow ${busy ? 0.7 : 2.6}s ease-in-out infinite` }}
                />
              </g>
            </g>
          </svg>
        )}
      </button>
    </div>
  );
}
